import { z } from 'zod';
import { AdfDocumentSchema } from './adf.js';
import { AccountIdSchema, DateOnlySchema, IssueKeySchema, TimestampSchema } from './common.js';
import { LocaleSchema } from './locale.js';
/**
 * Модели, которыми обменивются клиент и сервер. Это не сырые ответы Jira: лишнее отброшено,
 * ссылки на аватарки сведены к одной, категория статуса приведена к набору, который понимает
 * оформление таблицы из макета.
 */
export const UserRefSchema = z.object({
    accountId: AccountIdSchema,
    displayName: z.string(),
    avatarUrl: z.string().nullable(),
    active: z.boolean(),
});
export const ProjectSchema = z.object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
    avatarUrl: z.string().nullable(),
    /** Индекс в палитре разделов: цветная метка слева от названия. */
    colorIndex: z.number().int().min(0),
    issueCount: z.number().int().nonnegative().nullable(),
});
/**
 * Создание раздела — то есть проекта Jira.
 *
 * Ключ Jira задаёт сама: заглавная латиница и цифры, начинается с буквы, до 10 символов.
 * Проверяем его здесь, а не только на форме, потому что отказ Jira приходит текстом на языке
 * сайта и разобрать его на «ключ занят» и «ключ невалиден» уже нельзя.
 */
export const PROJECT_KEY_MAX = 10;
export const PROJECT_NAME_MAX = 80;
export const ProjectKeySchema = z
    .string()
    .trim()
    .min(2)
    .max(PROJECT_KEY_MAX)
    .regex(/^[A-Z][A-Z0-9]*$/u, 'ключ проекта — заглавная латиница и цифры');
export const ProjectCreateSchema = z.object({
    name: z.string().trim().min(1).max(PROJECT_NAME_MAX),
    key: ProjectKeySchema,
});
/** Категории статусов Jira. Определяют цвет плашки статуса в таблице. */
export const StatusCategorySchema = z.enum(['new', 'indeterminate', 'done', 'undefined']);
export const StatusRefSchema = z.object({
    id: z.string(),
    name: z.string(),
    category: StatusCategorySchema,
});
export const IssueTypeRefSchema = z.object({
    id: z.string(),
    name: z.string(),
    iconUrl: z.string().nullable(),
    subtask: z.boolean(),
    /**
     * Уровень иерархии Jira: −1 — подзадача, 0 — обычная задача, 1 — эпик, выше — надстройки
     * над эпиком. Ребёнок стоит ровно на уровень ниже родителя: у задачи — подзадачи,
     * у эпика — задачи.
     */
    hierarchyLevel: z.number().int(),
});
/** Уровень подзадачи: ниже неё в Jira ничего нет, и детей у неё не бывает. */
export const SUBTASK_LEVEL = -1;
/** Родитель задачи: для строки таблицы и цепочки над названием в карточке хватает ключа и названия. */
export const IssueParentRefSchema = z.object({
    key: IssueKeySchema,
    summary: z.string(),
});
export const PriorityRefSchema = z.object({
    id: z.string(),
    name: z.string(),
    /** Порядковый код P1…P4 — приоритет читается не только цветом. */
    rank: z.number().int().min(1).nullable(),
});
/** Строка таблицы задач. */
export const IssueSummarySchema = z.object({
    id: z.string(),
    key: IssueKeySchema,
    summary: z.string(),
    projectId: z.string(),
    projectKey: z.string(),
    projectName: z.string(),
    type: IssueTypeRefSchema.nullable(),
    status: StatusRefSchema.nullable(),
    assignee: UserRefSchema.nullable(),
    priority: PriorityRefSchema.nullable(),
    updated: TimestampSchema.nullable(),
    duedate: DateOnlySchema.nullable(),
    /** Родитель: у подзадачи — задача, у задачи — эпик. */
    parent: IssueParentRefSchema.nullable(),
});
/** Подзадачи задачи: сколько всего и сколько в категории «Готово». */
export const SubtaskProgressSchema = z.object({
    total: z.number().int().positive(),
    done: z.number().int().nonnegative(),
});
/**
 * Строка таблицы задач.
 *
 * Счётчик считается по всем подзадачам, а `children` — только те, что прошли отбор таблицы:
 * по умолчанию завершённых в таблице нет, и под родителем с «2 из 3» видна одна строка.
 * Счётчик и объясняет, куда делись остальные.
 */
export const IssueRowSchema = IssueSummarySchema.extend({
    subtaskProgress: SubtaskProgressSchema.nullable(),
    /** Подзадачи под строкой родителя; заполняются только в режиме дерева. */
    children: z.array(IssueSummarySchema),
});
/**
 * Дети задачи для карточки: подзадачи задачи или задачи эпика.
 * `truncated` — Jira отдала не всех, остальные смотрят в самой Jira.
 */
export const IssueChildrenSchema = z.object({
    items: z.array(IssueSummarySchema),
    truncated: z.boolean(),
});
/**
 * Вложение задачи.
 *
 * Файл лежит в Jira, а не в Jisy: `contentUrl` и `thumbnailUrl` ведут на сервер Jisy, который
 * забирает содержимое у Jira под токеном пользователя. Прямые адреса Jira дать нельзя —
 * они открываются только под её собственной сессией, а браузер про неё ничего не знает.
 */
export const AttachmentSchema = z.object({
    id: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    size: z.number().int().nonnegative(),
    created: TimestampSchema.nullable(),
    author: UserRefSchema.nullable(),
    contentUrl: z.string(),
    /** Миниатюра есть только у изображений — её рисует Jira. */
    thumbnailUrl: z.string().nullable(),
});
/** Задача в модальном окне. */
export const IssueDetailSchema = IssueSummarySchema.extend({
    description: AdfDocumentSchema.nullable(),
    /** HTML из expand=renderedFields — показывается до первого редактирования. */
    renderedDescription: z.string().nullable(),
    reporter: UserRefSchema.nullable(),
    created: TimestampSchema.nullable(),
    labels: z.array(z.string()),
    /** Значение поля процента, если тенант его настроил. */
    progress: z.number().int().min(0).max(100).nullable(),
    /** Вложения задачи: приходят вместе с карточкой, отдельного запроса не требуют. */
    attachments: z.array(AttachmentSchema),
    /** Даты плана из Jisy, если задача входит в чей-то план. */
    planRange: z
        .object({
        start: DateOnlySchema,
        end: DateOnlySchema,
        markedDays: z.number().int().positive(),
        planId: z.string(),
    })
        .nullable(),
});
export const TransitionSchema = z.object({
    id: z.string(),
    name: z.string(),
    to: StatusRefSchema.nullable(),
    /** Переход с экраном требует обязательных полей — предупреждаем до нажатия. */
    hasScreen: z.boolean(),
});
export const CommentSchema = z.object({
    id: z.string(),
    author: UserRefSchema.nullable(),
    body: AdfDocumentSchema.nullable(),
    renderedBody: z.string().nullable(),
    created: TimestampSchema.nullable(),
    updated: TimestampSchema.nullable(),
});
/** Участник тенанта: тот, кто заходил в Jisy, либо исполнитель из кэша задач. */
export const MemberSchema = z.object({
    accountId: AccountIdSchema,
    displayName: z.string(),
    avatarUrl: z.string().nullable(),
    /** true — заходил в Jisy, есть роль и настройки. */
    isAppUser: z.boolean(),
    role: z.enum(['admin', 'member']).nullable(),
    hasPlan: z.boolean(),
});
export const TenantSchema = z.object({
    id: z.string(),
    cloudId: z.string(),
    siteName: z.string(),
    siteUrl: z.string(),
    avatarUrl: z.string().nullable(),
    status: z.enum(['active', 'disconnected']),
});
/** Ответ GET /api/me. */
export const MeSchema = z.object({
    user: z.object({
        id: z.string(),
        accountId: AccountIdSchema,
        displayName: z.string(),
        email: z.string().nullable(),
        avatarUrl: z.string().nullable(),
        role: z.enum(['admin', 'member']),
        locale: LocaleSchema,
    }),
    tenant: TenantSchema,
});
/**
 * Ход синхронизации с Jira.
 *
 * Первая загрузка тенанта занимает минуты: справочники, разделы, затем задачи по каждому
 * разделу отдельно. Без этих чисел пользователь видит пустую таблицу и не понимает, сломалось
 * что-то или идёт работа.
 */
export const SyncStatusSchema = z.object({
    running: z.boolean(),
    /** Что делается сейчас; null — ничего не идёт. */
    stage: z.enum(['reference', 'projects', 'issues']).nullable(),
    startedAt: TimestampSchema.nullable(),
    lastSyncAt: TimestampSchema.nullable(),
    /**
     * Обойдено разделов из скольких. Ноль в `total` означает «сколько всего, ещё неизвестно» —
     * тогда полоса рисуется бегущей, а не заполняется до случайной отметки.
     */
    projectsDone: z.number().int().nonnegative(),
    projectsTotal: z.number().int().nonnegative(),
    issuesFetched: z.number().int().nonnegative(),
    /** Уже лежит в кэше: по нему видно, что показывать пустой экран больше незачем. */
    issuesInCache: z.number().int().nonnegative(),
    /** Синхронизация ни разу не доходила до конца — значит идёт самая первая. */
    firstRun: z.boolean(),
});
/**
 * Сколько цветов в палитре направлений: индекс раздела берётся по кругу, поэтому серверу нужно
 * только это число. Сами оттенки живут в `client/src/styles/tokens.css` (`--section-*`) —
 * держать их значения ещё и здесь значило бы иметь два источника одной палитры.
 */
export const PROJECT_COLOR_COUNT = 12;
//# sourceMappingURL=domain.js.map